<template>
  <input
    type="number"
    :inputmode="decimal ? 'decimal' : 'numeric'"
    min="0"
    :step="decimal ? '0.5' : '1'"
    :value="modelValue || ''"
    :placeholder="decimal ? '1.0' : '0'"
    :autofocus="autoFocus"
    :style="inputStyle"
    @change="handleChange"
    @wheel.prevent="handleWheel"
    @focus="e => e.target.select()"
  />
</template>

<script setup>
import { computed } from 'vue'

const props = defineProps({
  modelValue: { type: Number, default: 0 },
  size:       { type: String,  default: 'md' },
  decimal:    { type: Boolean, default: false },
  autoFocus:  { type: Boolean, default: false },
})

const emit = defineEmits(['update:modelValue'])

const cfg = {
  sm: { inputW: '44px', inputH: '28px', inputFs: '12px' },
  md: { inputW: '52px', inputH: '34px', inputFs: '14px' },
  lg: { inputW: '62px', inputH: '38px', inputFs: '15px' },
}

const c = computed(() => cfg[props.size] || cfg.md)

const inputStyle = computed(() => {
  const isZero = !props.modelValue
  return {
    width:        c.value.inputW,
    height:       c.value.inputH,
    padding:      '0 4px',
    textAlign:    'center',
    border:       `1.5px solid ${isZero ? 'var(--border-strong)' : 'var(--primary)'}`,
    background:   isZero ? 'var(--surface)' : 'var(--primary-softer)',
    borderRadius: '6px',
    fontFamily:   'var(--ff-head)',
    fontWeight:   700,
    fontSize:     c.value.inputFs,
    color:        isZero ? 'var(--text-3)' : 'var(--primary)',
    outline:      'none',
    boxSizing:    'border-box',
    flexShrink:   0,
    cursor:       'ns-resize',
  }
})

function handleChange(e) {
  const raw = e.target.value
  if (raw === '') { emit('update:modelValue', 0); return }
  const v = props.decimal
    ? Math.max(0, parseFloat(raw)  || 0)
    : Math.max(0, parseInt(raw)    || 0)
  emit('update:modelValue', v)
}

// Accumulate wheel ticks and flush once per animation frame
const STEP = computed(() => props.decimal ? 0.5 : 1)
let _wheelAcc = 0
let _wheelRaf = null
function handleWheel(e) {
  _wheelAcc += e.deltaY < 0 ? STEP.value : -STEP.value
  if (!_wheelRaf) {
    _wheelRaf = requestAnimationFrame(() => {
      const delta = _wheelAcc
      _wheelAcc = 0
      _wheelRaf = null
      if (delta !== 0) {
        const next = Math.max(0, (props.modelValue || 0) + delta)
        emit('update:modelValue', props.decimal ? Math.round(next * 10) / 10 : next)
      }
    })
  }
}
</script>

<style scoped>
input[type=number]::-webkit-outer-spin-button,
input[type=number]::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
input[type=number] { -moz-appearance: textfield; }
</style>
